import {useEffect, useState} from 'react';
import {AccessibilityInfo, Animated, Vibration} from 'react-native';

import {DARK, PIN_LENGTH, PIN_STATUS} from './PinInput.constant';

// Define Props Interface
interface UsePinInputProps {
  theme?: typeof DARK | string;
  onInputComplete: (pin: string) => void;
  onInputPin: (
    pin: string,
  ) => Promise<(typeof PIN_STATUS)[keyof typeof PIN_STATUS]>;
}

// Define Return Type
interface UsePinInputReturn {
  onBlur: () => void;
  onFocus: () => void;
  onChangeText: (code: string) => void;
  isWrongStyle: boolean;
  isWrongPin: boolean;
  slidesUpDownAnimations: Animated.Value[];
  value: string;
  focused: boolean;
}

export const usePinInput = ({
  theme = DARK,
  onInputComplete,
  onInputPin,
}: UsePinInputProps): UsePinInputReturn => {
  let timer: NodeJS.Timeout | null = null;

  const [focused, setFocused] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [slidesUpDownAnimations] = useState<Animated.Value[]>(
    Array.from({length: PIN_LENGTH}, () => new Animated.Value(0)),
  );

  const [isWrongPin, setIsWrongPin] = useState<boolean>(false);
  const [isWrongStyle, setIsWrongStyle] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const onError = () => {
    setIsWrongPin(true);

    if (theme === DARK) {
      setIsWrongStyle(true);
    }

    Vibration.vibrate(100);

    // After shake animations
    timer = setTimeout(() => {
      setValue('');
      setIsWrongPin(false);
    }, 250);
  };

  const onTextChange = (code: string) => setValue(code);

  const onFulfill = async (pin: string) => {
    const currentStatus = await onInputPin(pin);
    // Announce for accessibility users
    if (currentStatus === PIN_STATUS.confirm) {
      AccessibilityInfo.announceForAccessibility(
        'PIN entered. Please re-enter your PIN to confirm.',
      );
    }

    // After slide up and down animations
    timer = setTimeout(() => {
      if (currentStatus === PIN_STATUS.error) {
        onError();
        AccessibilityInfo.announceForAccessibility(
          'PIN entry failed. Please try again.',
        );
      }
      if (currentStatus === PIN_STATUS.confirm) {
        setValue('');
      }
      if (currentStatus === PIN_STATUS.enter) {
        onInputComplete(pin);
        // AccessibilityInfo.announceForAccessibility(
        //   'PIN confirmed successfully.',
        // );
      }
    }, 200);
  };

  const onChangeText = (code: string) => {
    if (code.length <= PIN_LENGTH) {
      onTextChange(code);
    }
    if (code.length === PIN_LENGTH) {
      onFulfill(code);
    }
  };

  const onFocus = () => {
    setFocused(true);
    setIsWrongPin(false);
  };

  const onBlur = () => setFocused(false);

  return {
    onBlur,
    onFocus,
    onChangeText,
    isWrongStyle,
    isWrongPin,
    slidesUpDownAnimations,
    value,
    focused,
  };
};
